
export interface User {
  name: string;
  cleanDate: string; // ISO Date string
  sponsorId?: string;
}

export interface SobrietyDate {
  id: string;
  label: string; // e.g., "Alcohol", "Nicotine", "Gambling"
  definition: string; // e.g., "Total abstinence from X"
  date: string;
}

export interface Step11Question {
  id: string;
  text: string;
  isDefault: boolean; // Default questions cannot be deleted
}

export interface ServiceEntry {
  id: string;
  date: string;
  action: string; // e.g., "Made coffee"
  category: 'meeting' | 'sponsorship' | 'call' | 'community' | 'other';
  notes?: string;
}

export interface MeetingEntry {
  id: string;
  date: string;
  type: 'AA' | 'NA' | 'CA' | 'HA' | 'Other';
  name?: string; // e.g. "Friday Night Lights"
  online: boolean;
}

export interface DefectScores {
  resentment: number;
  selfishness: number;
  dishonesty: number;
  fear: number;
}

export interface ReviewLog {
  id: string;
  date: string;
  answers: Record<string, string>; // Question ID -> Answer text
  aiResponse: ReviewResponse;
}

export interface SharingConfig {
  shareInventory: boolean;
  shareReviews: boolean;
  shareService: boolean;
  shareCircles: boolean;
  shareSobrietyDates: boolean;
}

export type StepStatus = 'locked' | 'active' | 'pending' | 'completed';

export interface FeedbackMessage {
  id: string;
  date: string;
  sender: 'user' | 'sponsor';
  text: string;
}

export interface StepProgress {
  stepNumber: number;
  status: StepStatus;
  homeworkCompleted: boolean;
  sponsorSignOffDate?: string;
  assignedHomework?: string; // Overrides default
  dueDate?: string; // ISO Date
  feedbackHistory: FeedbackMessage[];
}

export interface Protege {
  id: string;
  name: string;
  status: 'green' | 'yellow' | 'red';
  lastCheckIn: string;
  step: number;
  pendingReview?: boolean; // True if they are waiting for sponsor sign-off
  revisionRequested?: boolean; // True if sponsor sent it back
}

export interface InventoryItem {
  id: string;
  resentment: string;
  cause: string;
  affects: string[]; // e.g. ['Security', 'Self-esteem']
  myPart: string[]; // e.g. ['Selfish', 'Dishonest']
}

export interface UserProfile {
  id: string;
  firstName: string;
  lastInitial: string;
  email: string;
  emailVerified: boolean;
  phone: string;
  location: string; 
  sobrietyDates: SobrietyDate[];
  circleItems: CircleItem[];
  customQuestions: Step11Question[];
  serviceHistory: ServiceEntry[];
  meetingHistory: MeetingEntry[];
  reviewHistory: ReviewLog[];
  inventory: InventoryItem[]; // Added this field
  sharingConfig: SharingConfig;
  stepProgress: StepProgress[];
  proteges: Protege[];
}

export interface StepTemplateConfig {
    stepNum: number;
    homework: string;
    durationDays: number; // Time limit
}

export interface SponsorshipTemplate {
    id: string;
    name: string;
    description: string;
    configs: StepTemplateConfig[];
}

export interface ReviewResponse {
  insight: string;
  reflection: string;
  action: string;
  isHighRisk: boolean;
  sentimentScore: number; // 1-10 scale (1 = Crisis, 10 = Serene)
  defectScores: DefectScores; // 0-10 scale for specific defects (0 = None, 10 = Severe)
}

export interface GratitudeEntry {
  id: string;
  date: string;
  items: string[];
}

export enum CircleType {
  INNER = 'INNER', // Addictive behaviors (Red)
  MIDDLE = 'MIDDLE', // Slippery slope (Yellow)
  OUTER = 'OUTER', // Healthy behaviors (Green)
}

export interface CircleItem {
  id: string;
  text: string;
  type: CircleType;
}
